import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faSpinner } from "@fortawesome/free-solid-svg-icons";

import axiosInstance from "../../api/apiClient";
import { useDropdown } from "./useDropdown";
import { useAccess } from "../../permissions/access";
import { canOpen } from "../../permissions/registry";
import { searchActions, sectionTitle } from "../../navigation/actionRegistry";

const DETAILS = { Construction: "construction", Maintenance: "maintenance", Emergency: "emergency" };

/**
 * البحث الشامل في الترويسة (تحديث 60) — نفس المكان والشكل:
 * أوامر العمل برقمها أو رمز أمر المقاول أو رمز الاستشاري أو المقاول، والعقود/المشروعات، والمقاولين (من الخادم ومصفّاة بنطاق المستخدم)،
 * والصفحات والوظائف المصرح بها من سجل الإجراءات المركزي. لا يظهر ما لا يملك المستخدم صلاحيته.
 */
export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { open, setOpen, close, containerRef } = useDropdown();

  const [term, setTerm] = useState("");
  const [res, setRes] = useState(null);
  const [searching, setSearching] = useState(false);
  const requestId = useRef(0);

  useAccess();
  // الظهور كما كان: لمن يرى أوامر العمل (التحديث اليومي أو البحث)
  const canExecute = canOpen("/daily-execution");
  const canView = canOpen("/search-requests");
  const actions = term.trim().length >= 2 ? searchActions(term, 6) : [];

  const run = async (q) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) { setRes(null); return; }
    const current = ++requestId.current;
    setSearching(true);
    try {
      const { data } = await axiosInstance.get("global-search", { params: { q: trimmed, take: 6 } });
      if (current === requestId.current) setRes(data);
    } catch {
      if (current === requestId.current) setRes({ workOrders: [], contracts: [], contractors: [] });
    } finally {
      if (current === requestId.current) setSearching(false);
    }
  };

  useEffect(() => {
    const id = setTimeout(() => run(term), 300);
    return () => clearTimeout(id);
  }, [term]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (path) => { close(); setTerm(""); setRes(null); navigate(path); };
  const openOrder = (w) => {
    const type = DETAILS[w.type] || String(w.type).toLowerCase();
    go(`/project/${type}/${w.id}`);
  };

  if (!canExecute && !canView) return null;

  const wos = res?.workOrders || [];
  const contracts = res?.contracts || [];
  const contractors = res?.contractors || [];
  const empty = !searching && res && wos.length + contracts.length + contractors.length + actions.length === 0;

  return (
    <div className="ah-search" ref={containerRef}>
      <form onSubmit={(e) => { e.preventDefault(); setOpen(true); run(term); }} className="ah-search__form" role="search">
        <FontAwesomeIcon icon={faMagnifyingGlass} className="ah-search__icon" />
        <input
          type="search"
          value={term}
          onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
          onFocus={() => term.trim() && setOpen(true)}
          placeholder={t("header.quickSearchPlaceholder")}
          aria-label={t("common.search")}
          data-testid="gs-input"
        />
        {searching && <FontAwesomeIcon icon={faSpinner} spin className="ah-search__spinner" />}
      </form>

      {open && term.trim().length >= 2 && (
        <div className="ah-menu ah-menu--search" role="listbox" data-testid="gs-results">
          {searching && !res && <p className="ah-menu__empty">{t("header.searching")}</p>}
          {empty && <p className="ah-menu__empty">{t("common.noResults")}</p>}

          {wos.length > 0 && <div className="ah-menu__group" data-testid="gs-group-wo">أوامر العمل</div>}
          {wos.map((w) => (
            <button key={`${w.type}-${w.id}`} type="button" className="ah-menu__item ah-menu__item--stack" role="option" aria-selected={false}
              onClick={() => openOrder(w)} data-testid="gs-wo">
              <span className="ah-notif__title">{w.number || `#${w.id}`} · {w.typeLabel}{w.contractorCode ? ` · ${w.contractorCode}` : ""}</span>
              <span className="ah-notif__time">
                {[w.contractNumber && `عقد ${w.contractNumber}`, w.basket && `السلة: ${w.basket}`, w.contractor, `تطابق: ${w.match}`].filter(Boolean).join(" · ")}
              </span>
            </button>
          ))}

          {contracts.length > 0 && <div className="ah-menu__group">العقود والمشروعات</div>}
          {contracts.map((c) => (
            <button key={`c-${c.id}`} type="button" className="ah-menu__item ah-menu__item--stack" role="option" aria-selected={false}
              onClick={() => go(canOpen("/contract-settings") ? `/contract-settings?tab=contracts&contractId=${c.id}` : `/search-requests?contractId=${c.id}`)} data-testid="gs-contract">
              <span className="ah-notif__title">{c.contractNumber} — {c.name}</span>
              <span className="ah-notif__time">عقد / مشروع</span>
            </button>
          ))}

          {contractors.length > 0 && <div className="ah-menu__group">المقاولون</div>}
          {contractors.map((c) => (
            <button key={`k-${c.name}`} type="button" className="ah-menu__item ah-menu__item--stack" role="option" aria-selected={false}
              onClick={() => go(`/search-requests?search=${encodeURIComponent(c.name)}`)} data-testid="gs-contractor">
              <span className="ah-notif__title">{c.name}</span>
              <span className="ah-notif__time">مقاول{c.contracts?.length ? ` · العقود: ${c.contracts.join("، ")}` : ""}</span>
            </button>
          ))}

          {actions.length > 0 && <div className="ah-menu__group" data-testid="gs-group-pages">الصفحات والوظائف</div>}
          {actions.map((a) => (
            <button key={`a-${a.id}`} type="button" className="ah-menu__item ah-menu__item--stack" role="option" aria-selected={false}
              onClick={() => go(a.route)} data-testid="gs-action" data-action={a.id}>
              <span className="ah-notif__title"><i className={a.icon} aria-hidden="true" /> {a.title}</span>
              <span className="ah-notif__time">{sectionTitle(a.section)}{a.desc ? ` · ${a.desc}` : ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
